import React from "react";
import { Calendar, Clock, MapPin, ArrowRight, MessageCircle } from "lucide-react";
import { Badge } from "./ui/badge";
import { resolveImageUrl } from "../api";

const WHATSAPP_GROUP_LINK = "https://chat.whatsapp.com/Bevem3i6onIG44hohIp21j?mode=gi_t";

const formatDate = (dateStr) => {
  if (!dateStr) return "TBA";
  const date = new Date(dateStr + "T00:00:00");
  if (isNaN(date.getTime())) return "TBA";
  return date.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

const getMinPrice = (categories) => {
  if (!categories || categories.length === 0) return 0;
  return Math.min(...categories.map((c) => c.price));
};

const getTotalSeatsRemaining = (categories) => {
  if (!categories || categories.length === 0) return 0;
  return categories.reduce((sum, c) => sum + c.seats_remaining, 0);
};

/* ─── Upcoming Card (minimal — no details, promote WhatsApp) ─── */
const UpcomingCard = ({ experience, index }) => {
  return (
    <div
      data-testid={`upcoming-card-${experience.id}`}
      className="flex flex-col group transition-all duration-500 hover:-translate-y-1 animate-fade-in-up overflow-hidden"
      style={{
        background: "var(--np-surface)",
        border: "1px solid var(--np-border)",
      }}
      onMouseEnter={(e) => e.currentTarget.style.borderColor = "var(--np-accent-30)"}
      onMouseLeave={(e) => e.currentTarget.style.borderColor = "var(--np-border)"}
    >
      {experience.image_url && (
        <div className="relative w-full h-48 overflow-hidden">
          <img
            src={resolveImageUrl(experience.image_url)}
            alt={experience.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            onError={(e) => { e.target.style.display = "none"; }}
          />
          <div className="absolute inset-0" style={{ background: `linear-gradient(to top, var(--np-surface), transparent)` }} />
        </div>
      )}

      <div className="p-6 flex flex-col gap-4 flex-1">
        <h3 className="text-xl font-semibold leading-tight" style={{ color: "var(--np-text)" }}>
          {experience.title}
        </h3>

        <p className="text-base leading-relaxed flex-1" style={{ color: "var(--np-text-secondary)" }}>
          {experience.short_hook}
        </p>

        <a
          href={WHATSAPP_GROUP_LINK}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="get-notified-whatsapp-btn"
          className="inline-flex items-center justify-center gap-3 px-6 py-3 text-base font-medium transition-all duration-500 rounded-full mt-auto"
          style={{
            background: "var(--np-accent)",
            color: "#ffffff",
          }}
        >
          <MessageCircle size={18} /> Get Notified on WhatsApp
        </a>
      </div>
    </div>
  );
};

/* ─── Active Experience Card ─── */
const ExperienceCard = ({ experience, onBookNow, index }) => {
  const totalRemaining = getTotalSeatsRemaining(experience.categories);
  const isSoldOut = totalRemaining === 0;
  const isLowSeats = totalRemaining > 0 && totalRemaining <= 5;
  const minPrice = getMinPrice(experience.categories);

  return (
    <div
      data-testid={`experience-card-${experience.id}`}
      className="flex flex-col group transition-all duration-500 hover:-translate-y-1 animate-fade-in-up overflow-hidden"
      style={{
        animationDelay: `${index * 80}ms`,
        background: "var(--np-surface)",
        border: "1px solid var(--np-border)",
      }}
      onMouseEnter={(e) => e.currentTarget.style.borderColor = "var(--np-accent-30)"}
      onMouseLeave={(e) => e.currentTarget.style.borderColor = "var(--np-border)"}
    >
      {experience.image_url && (
        <div className="relative w-full h-48 overflow-hidden">
          <img
            src={resolveImageUrl(experience.image_url)}
            alt={experience.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            onError={(e) => { e.target.style.display = "none"; }}
          />
          <div className="absolute inset-0" style={{ background: `linear-gradient(to top, var(--np-surface), transparent)` }} />
          <div className="absolute top-3 left-3 flex gap-2">
            {experience.categories?.map((cat) => (
              <span
                key={cat.name}
                className="px-2 py-1 text-xs font-medium backdrop-blur-sm"
                style={{
                  background: "rgba(0,0,0,0.7)",
                  color: "rgba(255,255,255,0.8)",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                {cat.name}
              </span>
            ))}
          </div>
          {isLowSeats && (
            <Badge
              variant="outline"
              className="absolute top-3 right-3 text-xs rounded-none backdrop-blur-sm"
              style={{
                borderColor: "var(--np-accent-30)",
                color: "var(--np-accent)",
                background: "rgba(0,0,0,0.7)",
              }}
            >
              {totalRemaining} left
            </Badge>
          )}
          {isSoldOut && (
            <Badge
              variant="outline"
              className="absolute top-3 right-3 text-xs rounded-none backdrop-blur-sm"
              style={{
                borderColor: "rgba(239,68,68,0.4)",
                color: "#ef4444",
                background: "rgba(0,0,0,0.7)",
              }}
            >
              Sold Out
            </Badge>
          )}
        </div>
      )}

      <div className="p-6 flex flex-col gap-4 flex-1">
        <h3 className="text-xl font-semibold leading-tight" style={{ color: "var(--np-text)" }}>
          {experience.title}
        </h3>

        <div className="flex flex-col gap-2 text-sm" style={{ color: "var(--np-text-secondary)" }}>
          <div className="flex items-center gap-2">
            <Calendar size={14} style={{ color: "var(--np-accent)" }} />
            <span>{formatDate(experience.date)} &middot; {experience.start_time}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock size={14} style={{ color: "var(--np-accent)" }} />
            <span>{experience.duration}</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin size={14} style={{ color: "var(--np-accent)" }} />
            <span>{experience.venue}, {experience.city}</span>
          </div>
        </div>

        <p className="text-base leading-relaxed flex-1" style={{ color: "var(--np-text-secondary)" }}>
          {experience.short_hook}
        </p>

        <div
          className="flex items-center justify-between pt-4 mt-auto"
          style={{ borderTop: "1px solid var(--np-border)" }}
        >
          <div>
            <span className="text-sm" style={{ color: "var(--np-text-muted)" }}>From </span>
            <span className="text-2xl font-semibold" style={{ color: "var(--np-text)" }}>
              ₹{minPrice.toLocaleString("en-IN")}
            </span>
          </div>
          <button
            onClick={() => !isSoldOut && onBookNow(experience)}
            disabled={isSoldOut}
            data-testid={`book-now-btn-${experience.id}`}
            className={`inline-flex items-center gap-3 px-6 py-3 text-base font-medium transition-all duration-500 rounded-full ${
              isSoldOut ? "opacity-40 cursor-not-allowed" : ""
            }`}
            style={{
              background: isSoldOut ? "var(--np-elevated)" : "var(--np-cta)",
              color: isSoldOut ? "var(--np-text-muted)" : "var(--np-cta-text)",
            }}
          >
            {isSoldOut ? "Sold Out" : "Book Now"}
            {!isSoldOut && <ArrowRight size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
};

export const Experiences = ({ experiences, onBookNow }) => {
  const activeExperiences = experiences.filter((e) => e.is_active && !e.is_upcoming);
  const upcomingExperiences = experiences.filter((e) => e.is_active && e.is_upcoming);

  return (
    <section
      id="experiences"
      className="md:py-32 md:px-[7.6923%] grid-overlay py-24 px-5 bg-experiences-gradient grain-overlay"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-16">
          <h2
            className="text-[clamp(32px,4vw,48px)] font-semibold leading-tight mb-4"
            style={{ color: "var(--np-text)" }}
          >
            Upcoming Experiences
          </h2>
          <p className="text-lg max-w-[500px]" style={{ color: "var(--np-text-secondary)" }}>
            Handpicked events designed to spark meaningful connections.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {activeExperiences.map((exp, i) => (
            <ExperienceCard
              key={exp.id}
              experience={exp}
              onBookNow={onBookNow}
              index={i}
            />
          ))}
          {upcomingExperiences.map((exp, i) => (
            <UpcomingCard
              key={exp.id}
              experience={exp}
              index={activeExperiences.length + i}
            />
          ))}
        </div>

        {activeExperiences.length === 0 && upcomingExperiences.length === 0 && (
          <div className="text-center py-20" style={{ color: "var(--np-text-muted)" }}>
            <p className="text-xl">No experiences available right now.</p>
            <p className="mt-2">Check back soon for new events.</p>
          </div>
        )}
      </div>
    </section>
  );
};
